import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardList, Plus, Clock, Play, Square, Edit2, Trash2, AlertTriangle, ChevronDown, Eye, History } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useSupabaseQuery, useSupabaseInsert, useSupabaseUpdate, useSupabaseDelete, formatDate, formatTime, formatDuration } from '../hooks/useSupabase'
import { Button, Card, Input, Select, Textarea, Modal, EmptyState, PageHeader, Badge, Toast, CheckboxGroup } from '../components/ui/SharedUI'
import { ClockInModal } from '../components/ClockInModal'

const SHIFT_TYPES = [
  { value: 'direct_care', label: 'Direct care' },
  { value: 'no_show_covered', label: 'Covered no-show' },
  { value: 'training', label: 'Training' },
  { value: 'waiting_period', label: 'Waiting period' },
]

// Returns the LOCAL calendar date as YYYY-MM-DD, not the UTC date.
// Using new Date().toISOString().split('T')[0] returns the UTC day, which
// differs from the local day every evening in US time zones — that mismatch
// is what recorded a shift on the wrong calendar date.
function localDateString(d = new Date()) {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const TASKS = [
  { value: 'bathing', label: 'Bathing' },
  { value: 'dressing', label: 'Dressing' },
  { value: 'assist_self_admin_medication', label: 'Assist with self-admin medication' },
  { value: 'assist_toileting', label: 'Assist with toileting' },
  { value: 'bathing_bed_sponge', label: 'Bathing - bed/sponge bath' },
  { value: 'bathing_shower_tub', label: 'Bathing - shower/tub' },
  { value: 'bed_mobility', label: 'Bed mobility, turn, change position' },
  { value: 'bowel_bladder', label: 'Bowel/bladder care' },
  { value: 'catheter_hygiene', label: 'Catheter hygiene (advanced personal care)' },
  { value: 'clean_living_area', label: 'Clean living area' },
  { value: 'companionship', label: 'Companionship' },
  { value: 'dress_undress', label: 'Dress/undress' },
  { value: 'feeding_eating', label: 'Feeding/eating assistance' },
  { value: 'grooming', label: 'Grooming' },
  { value: 'hygiene', label: 'Hygiene' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'laundry_home', label: 'Laundry (home)' },
  { value: 'make_change_bed_linen', label: 'Make/change bed linen' },
  { value: 'meal_preparation', label: 'Meal preparation' },
  { value: 'mobility_transfer', label: 'Mobility/transfer' },
  { value: 'ostomy_care', label: 'Ostomy care' },
  { value: 'range_of_motion', label: 'Range of motion (ROM)' },
  { value: 'skin_care', label: 'Skin care/wound observation' },
  { value: 'transferring', label: 'Transferring' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'vital_signs', label: 'Vital signs' },
  { value: 'errands_shopping', label: 'Errands/shopping' },
  { value: 'exercise_assistance', label: 'Exercise assistance' },
  { value: 'respite_supervision', label: 'Respite/supervision' },
  { value: 'other', label: 'Other' },
]

const TYPE_BADGES = {
  direct_care: { label: 'Direct care', variant: 'default' },
  no_show_covered: { label: 'No-show', variant: 'coral' },
  training: { label: 'Training', variant: 'amber' },
  waiting_period: { label: 'Waiting period', variant: 'green' },
}

export function ShiftsPage() {
  const { user, profile } = useAuth()
  const { data: shifts, loading, refetch } = useSupabaseQuery('shifts', { orderBy: 'clock_in', ascending: false })
  const { data: recipients } = useSupabaseQuery('recipients', { column: 'owner_id', orderBy: 'name', ascending: true })
  const { insert } = useSupabaseInsert('shifts')
  const { update } = useSupabaseUpdate('shifts')
  const { remove } = useSupabaseDelete('shifts')

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [toast, setToast] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [activeShift, setActiveShift] = useState(null)
  const [elapsed, setElapsed] = useState(0)

  const [clockInOpen, setClockInOpen] = useState(false)
  const [pendingInitiatedAt, setPendingInitiatedAt] = useState(null)
  const [resumeDraft, setResumeDraft] = useState(null)
  const resumeHandledRef = useRef(null)

  const defaultShiftType = profile?.in_waiting_period ? 'waiting_period' : 'direct_care'

  const emptyForm = {
    recipient_id: '',
    date: localDateString(),
    clock_in_time: '',
    clock_out_time: '',
    shift_type: defaultShiftType,
    tasks: [],
    notes: '',
    incident: false,
  }
  const [form, setForm] = useState(emptyForm)

  // Only a confirmed clock-in counts as the active shift. A draft (button
  // pressed, modal not finished) is handled by the clock-in modal below.
  useEffect(() => {
    const active = shifts.find(s => s.is_active && s.confirmed_at)
    setActiveShift(active || null)
  }, [shifts])

  // If a clock-in was started but never finished (crash, closed tab, navigation),
  // pick the draft back up so the caregiver can finish or discard it.
  useEffect(() => {
    if (clockInOpen) return
    const draft = shifts.find(s => s.is_active && !s.confirmed_at)
    if (draft && resumeHandledRef.current !== draft.id) {
      resumeHandledRef.current = draft.id
      setResumeDraft(draft)
      setClockInOpen(true)
    }
  }, [shifts, clockInOpen])

  // Timer for active shift
  useEffect(() => {
    if (!activeShift) return
    const tick = () => setElapsed(Date.now() - new Date(activeShift.clock_in).getTime())
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [activeShift])

  function formatElapsed(ms) {
    const s = Math.floor(ms / 1000)
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    const sec = s % 60
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
  }

  function startClockIn() {
    if (activeShift) return
    setResumeDraft(null)
    setPendingInitiatedAt(new Date().toISOString())
    setClockInOpen(true)
  }

  async function handleClockOut() {
    if (!activeShift) return
    try {
      await update(activeShift.id, {
        clock_out: new Date().toISOString(),
        is_active: false,
      })
      setToast({ message: 'Clocked out', type: 'success' })
      setActiveShift(null)
      refetch()
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  function openManualEntry() {
    setEditing(null)
    setForm(emptyForm)
    setModalOpen(true)
  }

  function openEdit(shift) {
    setEditing(shift)
    const cin = new Date(shift.clock_in)
    const cout = shift.clock_out ? new Date(shift.clock_out) : null
    setForm({
      recipient_id: shift.recipient_id || '',
      date: shift.date,
      clock_in_time: cin.toTimeString().slice(0, 5),
      clock_out_time: cout ? cout.toTimeString().slice(0, 5) : '',
      shift_type: shift.shift_type,
      tasks: shift.tasks || [],
      notes: shift.notes || '',
      incident: shift.incident || false,
    })
    setModalOpen(true)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    try {
      const clockIn = new Date(`${form.date}T${form.clock_in_time}:00`).toISOString()
      const clockOut = form.clock_out_time
        ? new Date(`${form.date}T${form.clock_out_time}:00`).toISOString()
        : null

      const payload = {
        recipient_id: form.recipient_id || null,
        date: form.date,
        clock_in: clockIn,
        clock_out: clockOut,
        shift_type: form.shift_type,
        tasks: form.tasks,
        notes: form.notes || null,
        incident: form.incident,
        is_active: false,
      }

      if (editing) {
        await update(editing.id, payload)
        setToast({ message: 'Shift updated', type: 'success' })
      } else {
        await insert({ ...payload, caregiver_id: user.id })
        setToast({ message: 'Shift logged', type: 'success' })
      }
      setModalOpen(false)
      refetch()
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  async function handleDelete(id) {
    try {
      await remove(id)
      setDeleteConfirm(null)
      setToast({ message: 'Shift deleted', type: 'success' })
      refetch()
    } catch (err) {
      setToast({ message: err.message, type: 'error' })
    }
  }

  const recipientMap = Object.fromEntries(recipients.map(r => [r.id, r.name]))

  if (loading) return <div style={{ padding: '2rem', color: '#718096' }}>Loading...</div>

  return (
    <div style={{ padding: '2rem', maxWidth: '1000px' }}>
      <PageHeader
        title="Shifts"
        description="All your logged shifts, sortable and filterable."
        action={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to="/backfill" style={{ textDecoration: 'none' }}>
              <Button variant="secondary"><History size={16} /> Backfill</Button>
            </Link>
            <Button variant="secondary" onClick={openManualEntry}><Plus size={16} /> Log shift</Button>
            {activeShift ? (
              <Button variant="coral" onClick={handleClockOut}><Square size={16} /> Clock out</Button>
            ) : (
              <Button onClick={startClockIn}><Play size={16} /> Clock in</Button>
            )}
          </div>
        }
      />

      {/* Active shift banner */}
      {activeShift && (
        <Card style={{ marginBottom: '1rem', borderLeft: '3px solid #E07856', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#E07856', animation: 'pulse 2s infinite' }} />
            <div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C' }}>Active shift</div>
              <div style={{ fontSize: '13px', color: '#718096' }}>
                Started {formatTime(activeShift.clock_in)}
                {activeShift.recipient_id && ` · ${recipientMap[activeShift.recipient_id] || ''}`}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '20px', fontWeight: 500, color: '#E07856' }}>
              {formatElapsed(elapsed)}
            </span>
            <Button variant="coral" size="sm" onClick={handleClockOut}><Square size={14} /> Clock out</Button>
          </div>
        </Card>
      )}

      {/* Shifts list */}
      {shifts.length === 0 && !activeShift ? (
        <EmptyState
          icon={ClipboardList}
          title="No shifts logged"
          description="Clock in to start your first shift, or log one manually."
          action={
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
              <Button onClick={startClockIn}><Play size={16} /> Clock in now</Button>
              <Button variant="secondary" onClick={openManualEntry}><Plus size={16} /> Log manually</Button>
            </div>
          }
        />
      ) : (
        <div style={{ display: 'grid', gap: '8px' }}>
          {shifts.filter(s => !s.is_active).map(shift => {
            const badge = TYPE_BADGES[shift.shift_type] || TYPE_BADGES.direct_care
            return (
              <Card key={shift.id} style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                alignItems: 'center',
                gap: '12px',
                borderLeft: shift.authorship === 'observed'
                  ? '3px solid #8b5000'
                  : shift.incident
                    ? '3px solid #E07856'
                    : undefined,
                background: shift.authorship === 'observed' ? '#FDFCF7' : undefined,
              }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#1A202C' }}>{formatDate(shift.date)}</span>
                    {shift.authorship === 'observed' && (
                      <Badge variant="amber">
                        <Eye size={11} style={{ verticalAlign: 'middle', marginRight: '2px' }} /> Observed
                      </Badge>
                    )}
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    {shift.incident && <Badge variant="coral"><AlertTriangle size={11} /> Incident</Badge>}
                    {shift.locked_at && <Badge variant="gray">Locked</Badge>}
                    {shift.is_backfilled && !shift.locked_at && (
                      <Badge variant="gray">
                        <History size={11} style={{ verticalAlign: 'middle', marginRight: '2px' }} /> Backfilled
                      </Badge>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: '#718096' }}>
                    {formatTime(shift.clock_in)} – {shift.clock_out ? formatTime(shift.clock_out) : 'in progress'}
                    {' · '}{formatDuration(shift.clock_in, shift.clock_out)}
                    {shift.recipient_id && ` · ${recipientMap[shift.recipient_id] || ''}`}
                  </div>
                  {shift.authorship === 'observed' && (
                    <div style={{ fontSize: '12px', color: '#8b5000', marginTop: '2px' }}>
                      Worked by {shift.observed_caregiver_name}
                      {shift.evidence_source && ` · Basis: ${shift.evidence_source.replace(/_/g, ' ')}`}
                    </div>
                  )}
                  {shift.notes && (
                    <div style={{ fontSize: '13px', color: '#4A5568', marginTop: '4px', fontStyle: 'italic' }}>
                      {shift.notes.length > 100 ? shift.notes.slice(0, 100) + '...' : shift.notes}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: '15px', fontWeight: 600, color: '#1A202C', textAlign: 'right', minWidth: '60px' }}>
                  {shift.clock_out
                    ? `${(((new Date(shift.clock_out) - new Date(shift.clock_in)) / 3600000)).toFixed(1)}h`
                    : '—'}
                </div>
                {!shift.locked_at && (
                  <div style={{ display: 'flex', gap: '2px' }}>
                    <Button variant="ghost" size="sm" onClick={() => openEdit(shift)}><Edit2 size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(shift)}><Trash2 size={14} /></Button>
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {/* Manual entry / edit modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit shift' : 'Log a shift'} width={520}>
        <form onSubmit={handleSubmit}>
          {recipients.length > 0 && (
            <Select
              label="Care recipient"
              id="recipient_id"
              value={form.recipient_id}
              onChange={e => setForm({ ...form, recipient_id: e.target.value })}
              options={[{ value: '', label: 'Select recipient...' }, ...recipients.map(r => ({ value: r.id, label: r.name }))]}
            />
          )}
          <Input
            label="Date"
            id="date"
            type="date"
            value={form.date}
            onChange={e => setForm({ ...form, date: e.target.value })}
            required
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Input
              label="Clock in"
              id="clock_in_time"
              type="time"
              value={form.clock_in_time}
              onChange={e => setForm({ ...form, clock_in_time: e.target.value })}
              required
            />
            <Input
              label="Clock out"
              id="clock_out_time"
              type="time"
              value={form.clock_out_time}
              onChange={e => setForm({ ...form, clock_out_time: e.target.value })}
            />
          </div>
          <Select
            label="Shift type"
            id="shift_type"
            value={form.shift_type}
            onChange={e => setForm({ ...form, shift_type: e.target.value })}
            options={SHIFT_TYPES}
          />
          <CheckboxGroup
            label="Tasks performed"
            options={TASKS}
            selected={form.tasks}
            onChange={tasks => setForm({ ...form, tasks })}
          />
          <Textarea
            label="Notes"
            id="notes"
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="What happened during this shift?"
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem', fontSize: '14px', color: '#4A5568', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.incident}
              onChange={e => setForm({ ...form, incident: e.target.checked })}
            />
            <AlertTriangle size={14} style={{ color: '#E07856' }} />
            Flag as incident
          </label>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit">{editing ? 'Save changes' : 'Log shift'}</Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete shift?" width={400}>
        <p style={{ color: '#4A5568', marginBottom: '1rem', lineHeight: 1.6 }}>
          This will permanently delete the shift from {formatDate(deleteConfirm?.date)}. This action cannot be undone.
        </p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Button variant="secondary" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="danger" onClick={() => handleDelete(deleteConfirm.id)}>Delete</Button>
        </div>
      </Modal>

      {clockInOpen && (
        <ClockInModal
          user={user}
          recipients={recipients}
          defaultShiftType={defaultShiftType}
          initiatedAt={pendingInitiatedAt}
          existingDraft={resumeDraft}
          onClose={(changed) => { setClockInOpen(false); setResumeDraft(null); if (changed) refetch() }}
          onToast={(t) => setToast(t)}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  )
}
